import { z } from 'zod';
import { countCharacters } from './text';

/** Most drafts an account can keep. */
export const MAX_DRAFTS = 100;
export const MAX_DRAFT_NAME_LENGTH = 80;
/**
 * Longest draft an account keeps, in Unicode code points of its Markdown. Ten rewrites' worth: a
 * draft can be longer than one rewrite, which then takes part of it at a time.
 */
export const MAX_DRAFT_LENGTH = 50_000;

/**
 * A draft a writer keeps in their account, to open again later: what they called it, and its
 * text, Markdown as the editor writes it.
 */
export const DraftInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(MAX_DRAFT_NAME_LENGTH)
      .describe('What the draft is called, as the list of drafts shows it.'),
    // Kept as written: spaces at the start of a line can matter in Markdown.
    text: z
      .string()
      .refine((text) => text.trim().length > 0, 'The draft has no text.')
      .refine(
        (text) => countCharacters(text) <= MAX_DRAFT_LENGTH,
        `A draft can have up to ${String(MAX_DRAFT_LENGTH)} characters.`,
      )
      .describe('The draft, in Markdown, as the editor writes its formatting.'),
  })
  .meta({ id: 'DraftInput' });

export type DraftInput = z.infer<typeof DraftInputSchema>;

/** A draft as the list shows it, without its text. */
export const DraftSummarySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    words: z
      .int()
      .nonnegative()
      .describe('How many words the draft has, as the editor counts them.'),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().describe('When the draft was last saved.'),
  })
  .meta({ id: 'DraftSummary' });

export type DraftSummary = z.infer<typeof DraftSummarySchema>;

export const DraftSchema = DraftSummarySchema.extend({ text: z.string() }).meta({ id: 'Draft' });

export type Draft = z.infer<typeof DraftSchema>;

export const DraftResponseSchema = z.object({ draft: DraftSchema }).meta({ id: 'DraftResponse' });

export const DraftsResponseSchema = z
  .object({ drafts: z.array(DraftSummarySchema).describe('Last saved first.') })
  .meta({ id: 'Drafts' });
